<script setup>
import { ref, onMounted, computed, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useRevendaStore } from '@/stores/revenda';
import { useNotificationStore } from '@/stores/notification';
import { useFormaPagamentoStore } from '@/stores/formaPagamento';
import { useTipoProdutosStore } from '@/stores/tipoProdutos'; 
import { useFabricantesStore } from '@/stores/fabricantes';
import { useEnderecoStore } from '@/stores/endereco';
import { formatarExibicaoProduto } from '@/utils/tipoProduto';

import EditProdutoRevendaModal from '@/components/modals/EditProdutoRevendaModal.vue';
import MarcaLogo from '@/components/marcas/MarcaLogo.vue';

const revendaStore = useRevendaStore();
const notificationStore = useNotificationStore();
const formaPagamentoStore = useFormaPagamentoStore();
const tipoProdutosStore = useTipoProdutosStore();
const fabricantesStore = useFabricantesStore();
const enderecoStore = useEnderecoStore();
const router = useRouter();
const route = useRoute();

const showEditProdutoModal = ref(false);
const selectedProduto = ref(null);

const revenda = computed(() => revendaStore.revenda);
const error = computed(() => revendaStore.error);
const loading = computed(() => revendaStore.loading);

const selectedFormas = ref([]);

const buscandoCep = ref(false);

const form = reactive({
  id: null,
  nomefantasia: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  dtcadastro: '',
  observacao: '',
  ptoreferencia: '',
  ativo: '1',
  formas_pagamento: [],
  lat: '',
  lon: ''
});

const saving = ref(false);

const productStatusLoading = ref(false);

const sortBy = ref({ key: '', order: 'asc' });

// Variables to track which fields were automatically filled by CEP lookup
const autoFilledLogradouro = ref(false);
const autoFilledBairro = ref(false);

// Add a flag to prevent initial CEP lookup
const initialLoad = ref(true);

const buscarEnderecoPorCep = async () => {
  if (!form.cep || form.cep.length < 8) return;
  
  buscandoCep.value = true;
  
  try {
    const response = await enderecoStore.buscarEnderecoPorCep(form.cep);
    
    if (response.success && response.data) {
      const endereco = response.data;
      
      // Mark which fields are auto-filled by the endpoint
      if (endereco.logradouro) {
        form.logradouro = endereco.logradouro;
        autoFilledLogradouro.value = true;
      } else {
        autoFilledLogradouro.value = false;
      }
      
      if (endereco.bairro) {
        form.bairro = endereco.bairro;
        autoFilledBairro.value = true;
      } else {
        form.bairro = "";
        autoFilledBairro.value = false;
      }
      
      // These fields are always readonly
      form.cidade = endereco.cidade || '';
      form.estado = endereco.uf || '';
      
      form.lat = endereco.latitude || '';
      form.lon = endereco.longitude || '';
    } else {
      notificationStore.showNotification(response.message || 'CEP não encontrado', 'warning');
    }
  } catch (err) {
    console.error('Erro ao buscar CEP:', err);
    notificationStore.showNotification('Erro ao buscar CEP', 'error');
  } finally {
    buscandoCep.value = false;
  }
};

const formatarCep = () => {
  let cep = form.cep.replace(/\D/g, '');
  
  if (cep.length > 5) {
    form.cep = `${cep.substring(0, 5)}-${cep.substring(5, 8)}`;
  } else {
    form.cep = cep;
  }
};

watch(() => form.cep, (newCep, oldCep) => {
  // Only trigger CEP lookup if it's not the initial load and the CEP has been changed
  if (!initialLoad.value && newCep?.replace(/\D/g, '') !== oldCep?.replace(/\D/g, '') && newCep?.length == 9) {
    buscarEnderecoPorCep();
  }
});

watch(revenda, (newVal) => {
  if (newVal) {
    Object.assign(form, {
      id: newVal.id,
      nomefantasia: newVal.nomefantasia || '',
      cep: newVal.cep || '',
      logradouro: newVal.logradouro || '',
      numero: newVal.numero || '',
      complemento: newVal.complemento || '',
      bairro: newVal.bairro || '',
      cidade: newVal.cidade || '',
      estado: newVal.estado || '',
      dtcadastro: newVal.dtcadastro || '',
      observacao: newVal.observacao || '',
      ptoreferencia: newVal.ptoreferencia || '',
      ativo: newVal.ativo || '0',
      formas_pagamento: newVal.formas_pagamento 
        ? newVal.formas_pagamento.map(fp => fp.id) 
        : [],
      lat: newVal.lat || '',
      lon: newVal.lon || ''
    });
    
    // Set the autoFilled flags based on whether fields have values
    autoFilledLogradouro.value = !!newVal.logradouro;
    autoFilledBairro.value = !!newVal.bairro;
    
    const paymentMethodIds = newVal.formas_pagamento ? newVal.formas_pagamento.map(fp => fp.id) : [];
    selectedFormas.value = [...paymentMethodIds];
    
    // Set initialLoad to false after a small delay
    setTimeout(() => {
      initialLoad.value = false;
    }, 500);
  }
});

watch(selectedFormas, (newVal) => {
  form.formas_pagamento = [...newVal];
});

onMounted(async () => {
  const revendaId = route.params.id;
  if (!revendaId) {
    notificationStore.showNotification('ID da revenda não fornecido', 'error');
    router.push({ name: 'revendas' });
    return;
  }
  try {
    await revendaStore.buscarRevendaPorId(revendaId);
    await formaPagamentoStore.fetchFormasPagamento();
    await tipoProdutosStore.buscarTipoProdutos();
    await fabricantesStore.buscarFabricantes();
  } catch (err) {
    console.error('Erro ao carregar dados da revenda:', err);
  }
});

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const salvar = async () => {
  try {
    const response = await revendaStore.atualizarRevenda(form.id, form);
    if (response.success) {
      notificationStore.showNotification(response.message || 'Revenda atualizada com sucesso!', 'success');
    } else {
      notificationStore.showNotification(response.message || 'Erro ao atualizar revenda', 'error');
    }
  } catch (err) {
    console.error('Erro ao salvar revenda:', err);
    notificationStore.showNotification('Erro ao atualizar revenda', 'error');
  }
};

const voltarParaLista = () => {
  const revendedorId = route.query.revendedorId || route.params.revendedorId;
  
  if (revendedorId) {
    router.push({ 
      name: 'revendedor-detalhes', 
      params: { id: revendedorId }
    });
  } else {
    router.push({ name: 'revendas' });
  }
};

const toggleFormaPagamento = async (value, isChecked) => {
  
  if (!form.id) return;
  
  try {
    if (isChecked) {
      const res = await revendaStore.adicionarFormaPagamento(form.id, value);
      if (res.success) {
        notificationStore.showNotification(res.message || 'Forma de pagamento adicionada com sucesso!', 'success');
        if (!selectedFormas.value.includes(value)) {
          selectedFormas.value = [...selectedFormas.value, value];
        }
      } else {
        notificationStore.showNotification(res.message || 'Erro ao adicionar forma de pagamento', 'error');
      }
    } else {
      const res = await revendaStore.removerFormaPagamento(form.id, value);
      if (res.success) {
        notificationStore.showNotification(res.message || 'Forma de pagamento removida com sucesso!', 'success');
        selectedFormas.value = selectedFormas.value.filter(v => v !== value);
      } else {
        notificationStore.showNotification(res.message || 'Erro ao remover forma de pagamento', 'error');
      }
    }
  } catch (error) {
    console.error('Erro ao processar forma de pagamento:', error);
    notificationStore.showNotification('Erro ao processar operação', 'error');
  }
};

const fpgSelecionada = (value) => {
  return selectedFormas.value.map(Number).includes(Number(value));
};

const editarProduto = (produto) => {
  selectedProduto.value = { ...produto };
  showEditProdutoModal.value = true;
};

const novoProduto = () => {
  selectedProduto.value = {
    id: null,
    valor: 0,
    valorportaria: 0,
    statusparcela: 0,
    qtdparcela: 1,
    statusdesconto: 0,
    valordesconto: 0,
    status: 1
  };
  showEditProdutoModal.value = true;
};

const handleToggleProdutoStatus = async (produto) => {
  try {
    if (!revenda.value || !revenda.value.id || !produto.id) {
      throw new Error('Dados do produto ou revenda inválidos');
    }
    
    productStatusLoading.value = true;
    
    const newStatus = produto.status === '1' || produto.status === 1 ? 0 : 1;
    
    const produtoData = {
      id: produto.id,
      status: newStatus
    };
    
    const response = await revendaStore.atualizarProdutoRevenda(revenda.value.id, produtoData);
    
    if (response.success) {
      notificationStore.showNotification(
        newStatus === 0
          ? 'Produto desativado com sucesso!'
          : 'Produto ativado com sucesso!',
        'success'
      );
    } else {
      throw new Error(response.message || 'Erro ao alterar status do produto');
    }
  } catch (err) {
    console.error('Erro ao alterar status do produto:', err);
    notificationStore.showNotification('Erro ao alterar status do produto', 'error');
  } finally {
    productStatusLoading.value = false;
  }
};

const atualizarProduto = async (produto) => {
  try {
    if (!revenda.value || !revenda.value.id) {
      throw new Error('ID da revenda inválido');
    }
    
    loading.value = true;
    
    const produtoData = {
      id: produto.id,
      valor: produto.valor,
      valorportaria: produto.valorportaria,
      statusparcela: produto.statusparcela,
      qtdparcela: produto.qtdparcela || 1,
      valor_total_parcelamento: produto.valor_total_parcelamento || 0,
      statusdesconto: produto.statusdesconto,
      valordesconto: produto.valordesconto || 0,
      status: produto.status
    };
    
    if (!produto.id) {
      produtoData.idfabricante = produto.fabricante.id;
      produtoData.idtipoproduto = produto.tipoproduto.id;
    }
    
    const response = await revendaStore.atualizarProdutoRevenda(revenda.value.id, produtoData);
    
    if (response.success) {
      notificationStore.showNotification(
        produto.id ? 'Produto atualizado com sucesso!' : 'Produto cadastrado com sucesso!',
        'success'
      );
      showEditProdutoModal.value = false;
    } else {
      throw new Error(response.message || 'Erro ao processar produto');
    }
  } catch (err) {
    console.error('Erro ao atualizar produto:', err);
    notificationStore.showNotification('Erro ao atualizar produto', 'error');
  } finally {
    loading.value = false;
  }
};

const obterExibicaoProduto = (tipoproduto) => {
  return formatarExibicaoProduto(tipoproduto);
};

const sortedProdutos = computed(() => {
  if (!revenda.value?.produtos || !sortBy.value.key) return revenda.value?.produtos || [];
  
  return [...revenda.value.produtos].sort((a, b) => {
    let aValue, bValue;
    
    if (sortBy.value.key === 'fabricante.nome') {
      aValue = a.fabricante?.nome || '';
      bValue = b.fabricante?.nome || '';
    } 
    else if (sortBy.value.key === 'produto') {
      aValue = formatarExibicaoProduto(a.tipoproduto);
      bValue = formatarExibicaoProduto(b.tipoproduto);
    } 
    else {
      aValue = a[sortBy.value.key];
      bValue = b[sortBy.value.key];
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const compareResult = aValue.localeCompare(bValue, 'pt-BR', { sensitivity: 'base' });
      return sortBy.value.order === 'asc' ? compareResult : -compareResult;
    }
    
    return sortBy.value.order === 'asc' ? aValue - bValue : bValue - aValue;
  });
});

// Computed properties para controlar a readonly property dos campos
const isLogradouroReadonly = computed(() => {
  return autoFilledLogradouro.value;
});

const isBairroReadonly = computed(() => {
  return autoFilledBairro.value;
});

</script>

<template>
  <div>
    <div v-if="loading && !revenda" class="d-flex justify-center align-center" style="min-height: 300px;">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
    <v-alert v-else-if="error" type="error" class="mb-4">
      {{ error }}
      <div class="mt-2">
        <v-btn color="primary" @click="voltarParaLista">Voltar para lista</v-btn>
      </div>
    </v-alert>
    
    <div v-else-if="revenda">
      <v-row class="mb-4">
        <v-col cols="12" sm="8">
          <div class="d-flex align-center">
            <h1 class="text-h4 mb-0">Dados da Revenda</h1>
          </div>
          <div class="text-subtitle-1 text-medium-emphasis mt-1">
            ID: {{ revenda.id }} | Revendedor ID: {{ revenda.idrevendedor }}
          </div>
        </v-col>
        <v-col cols="12" sm="4" class="d-flex justify-end align-center">
          <v-btn
            color="primary"
            variant="outlined"
            prepend-icon="mdi-map"
            :to="{ name: 'revenda-mapa', params: { id: revenda.id } }"
          >
            Ver Mapa da Revenda
          </v-btn>
        </v-col>
      </v-row>
      
      <v-form @submit.prevent="salvar">
        <v-card class="mb-4">
          <v-card-title>Informações Gerais</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.nomefantasia"
                  label="Nome Fantasia"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-store"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.cep"
                  label="CEP"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-map-marker"
                  @input="formatarCep"
                  @blur="buscarEnderecoPorCep"
                  :loading="buscandoCep"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.logradouro"
                  label="Logradouro"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-road"
                  :readonly="isLogradouroReadonly"
                  :class="{ 'readonly-field': isLogradouroReadonly }"
                  :bg-color="isLogradouroReadonly ? 'grey-lighten-3' : undefined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.numero"
                  label="Número"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-numeric"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="form.complemento"
                  label="Complemento"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-home-variant"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.bairro"
                  label="Bairro"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-office-building"
                  :readonly="isBairroReadonly"
                  :class="{ 'readonly-field': isBairroReadonly }"
                  :bg-color="isBairroReadonly ? 'grey-lighten-3' : undefined"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.ptoreferencia"
                  label="Referência"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-map-marker"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.cidade"
                  label="Cidade"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-city"
                  readonly
                  class="readonly-field"
                  bg-color="grey-lighten-3"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.estado"
                  label="Estado"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-map"
                  readonly
                  class="readonly-field"
                  bg-color="grey-lighten-3"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="form.observacao"
                  label="Observação"
                  variant="outlined"
                  hide-details="auto"
                  prepend-inner-icon="mdi-note-text"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title>Formas de Pagamento</v-card-title>
          <v-card-text>
            <v-row v-if="formaPagamentoStore.data && formaPagamentoStore.data.length">
              <v-col
                v-for="forma in formaPagamentoStore.data"
                :key="forma.value"
                cols="auto"
              >
                <v-checkbox
                  :label="forma.title"
                  :model-value="fpgSelecionada(forma.value)"
                  @update:model-value="checked => toggleFormaPagamento(forma.value, checked)"
                  hide-details="auto"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12">
                Nenhuma forma de pagamento cadastrada.
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
            <v-card-title class="d-flex justify-space-between align-center">
            <span>Produtos</span>
            <v-btn
              color="primary"
              @click="novoProduto"
              prepend-icon="mdi-plus"
            >
              Novo Produto
            </v-btn>
            </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="[
                { 
                  title: 'Fabricante', 
                  value: 'fabricante.nome', 
                  sortable: true,
                  align: 'start',
                  width: '200px'
                },
                { 
                  title: 'Produto', 
                  value: 'produto', 
                  sortable: true,
                  align: 'start'  
                },
                { title: 'Valor', value: 'valor', align: 'end' },
                { title: 'Valor Portaria', value: 'valorportaria', align: 'end' },
                { title: 'Parcelamento', value: 'parcelamento' },
                { title: 'Desconto no 2º', value: 'desconto', align: 'end' },
                { title: 'Status', value: 'status' },
                { title: 'Ações', value: 'actions', sortable: false, align: 'center' }
              ]"
              :items="sortedProdutos"
              item-value="id"
              class="elevation-1"
              :sort-by="sortBy.key ? [{ key: sortBy.key, order: sortBy.order }] : []"
              @update:sort-by="newSort => sortBy.key = newSort[0]?.key || ''"
              @update:sort-order="newOrder => sortBy.order = newOrder[0] || 'asc'"
            >
              <template #item.fabricante.nome="{ item }">
                <MarcaLogo 
                  :logomarca="item.fabricante?.logo || ''" 
                  :fabricante="item.fabricante?.nome || ''" 
                  :width="120"
                  :height="40"
                />
              </template>
              <template #item.produto="{ item }">
                {{ obterExibicaoProduto(item.tipoproduto) }}
              </template>
              <template #item.valor="{ item }">
                {{ formatCurrency(item.valor) }}
              </template>
              <template #item.valorportaria="{ item }">
                {{ formatCurrency(item.valorportaria) }}
              </template>
              <template #item.parcelamento="{ item }">
                {{ item.statusparcela === 1 ? item.valor_total_parcelamento+" em até "+item.qtdparcela+"x" : "-" }} 
              </template>
              <template #item.desconto="{ item }">
                <span v-if="item.statusdesconto === 1">{{ formatCurrency(item.valordesconto) }}</span>
                <span v-else>-</span>
              </template>
              <template #item.status="{ item }">
                <v-chip
                  :color="item.status === 1 ? 'success' : 'error'"
                >
                  {{ item.status === 1 ? 'Ativo' : 'Inativo' }}
                </v-chip>
              </template>
              <template #item.actions="{ item }">
                <div class="d-flex justify-center gap-2">
                  <v-icon
                    :color="item.status === 1 ? 'error' : 'success'"
                    size="large"
                    class="mr-2 cursor-pointer"
                    :title="item.status === 1 ? 'Desativar produto' : 'Ativar produto'"
                    @click="handleToggleProdutoStatus(item)"
                  >
                    {{ item.status === 1 ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}
                  </v-icon>

                  <v-icon
                    size="large"
                    color="primary"
                    class="ml-2 cursor-pointer"
                    title="Editar produto"
                    @click="editarProduto(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-row>
          <v-col cols="12" sm="6">
            <v-btn type="submit" color="primary" block :loading="saving" prepend-icon="mdi-content-save">
              Salvar Alterações
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6">
            <v-btn variant="outlined" color="grey" block @click="voltarParaLista" prepend-icon="mdi-arrow-left">
              Voltar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </div>
    
    <EditProdutoRevendaModal
      v-model:open="showEditProdutoModal"
      :produto="selectedProduto"
      :tipos-produto="tipoProdutosStore.tipoProdutos"
      :fabricantes="fabricantesStore.fabricantes"
      @updated="atualizarProduto"
    />
  </div>
</template>

<style scoped>
:deep(.v-data-table td) {
  vertical-align: middle;
}

.readonly-field {
  pointer-events: none;
  user-select: none;
}

:deep(.readonly-field .v-field__input) {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
